import { z } from 'zod/v4';
import {
  IJourneyBoard,
  IJourneyLeg,
  IJourneyLocation,
  IJourneyOperator,
  IJourneyProduct,
  IJourneyStop,
  IJourneyTrip,
  JourneyLocationId,
  LatitudeCoordinate,
  LongitudeCoordinate,
} from './journey-planning-types.interfaces';
import { EFA_TO_FARE_VIA_SCHEMA } from './efa-to-fare.validations';

export const JOURNEY_STOP_SCHEMA: z.ZodType<IJourneyStop> = z.object({
  id: z.string(),
  externalId: z.string(),
  name: z.string(),
  longitudeCoord: z
    .number()
    .transform((n: number) => n as LongitudeCoordinate)
    .default(0 as LongitudeCoordinate),
  latitudeCoord: z
    .number()
    .transform((n: number) => n as LatitudeCoordinate)
    .default(0 as LatitudeCoordinate),
  timezoneOffset: z.number(),
  weight: z.number(),
});

export const JOURNEY_STOPS_SCHEMA: z.ZodType<IJourneyStop[]> =
  z.array(JOURNEY_STOP_SCHEMA);

export const JOURNEY_OPERATOR_SCHEMA: z.ZodType<IJourneyOperator> = z.object({
  id: z.string(),
  name: z.string(),
  url: z.string(),
});

export const OPERATOR_SCHEMA: z.ZodType<IJourneyOperator> = z.object({
  id: z.string(),
  name: z.string(),
  url: z.string(),
});

export const JOURNEY_PRODUCT_SCHEMA: z.ZodType<IJourneyProduct> = z.object({
  operator: JOURNEY_OPERATOR_SCHEMA,
});

export const JOURNEY_LOCATION_SCHEMA: z.ZodType<IJourneyLocation> = z.object({
  id: z
    .string()
    .transform((s: string) => s as JourneyLocationId)
    .default('' as JourneyLocationId),
  externalId: z.string(),
  locationReferenceId: z.string(),
  name: z.string(),
  type: z.string(),
  longitudeCoord: z
    .number()
    .transform((n: number) => n as LongitudeCoordinate)
    .default(0 as LongitudeCoordinate),
  latitudeCoord: z
    .number()
    .transform((n: number) => n as LatitudeCoordinate)
    .default(0 as LatitudeCoordinate),
  time: z.string(),
  date: z.string(),
});

export const JOURNEY_LEG_SCHEMA: z.ZodType<IJourneyLeg> = z.object({
  id: z.string(),
  name: z.string(),
  direction: z.string(),
  type: z.string(),
  distance: z.number(),
  operator: OPERATOR_SCHEMA,
  origin: JOURNEY_LOCATION_SCHEMA,
  destination: JOURNEY_LOCATION_SCHEMA,
});

export const JOURNEY_LEGS_SCHEMA: z.ZodType<IJourneyLeg[]> =
  z.array(JOURNEY_LEG_SCHEMA);

export const JOURNEY_TRIP_SCHEMA: z.ZodType<IJourneyTrip> = z.object({
  id: z.string(),
  origin: JOURNEY_LOCATION_SCHEMA,
  destination: JOURNEY_LOCATION_SCHEMA,
  legs: JOURNEY_LEGS_SCHEMA,
  tariffSegments: z.array(EFA_TO_FARE_VIA_SCHEMA).optional(),
});

export const JOURNEY_TRIPS_SCHEMA: z.ZodType<IJourneyTrip[]> =
  z.array(JOURNEY_TRIP_SCHEMA);

export const JOURNEY_BOARD_SCHEMA: z.ZodType<IJourneyBoard> = z.object({
  name: z.string(),
  stop: z.string(),
  type: z.string(),
  stopId: z.string(),
  externalStopId: z.string(),
  longitudeCoord: z
    .number()
    .transform((n: number) => n as LongitudeCoordinate)
    .default(0 as LongitudeCoordinate),
  latitudeCoord: z
    .number()
    .transform((n: number) => n as LatitudeCoordinate)
    .default(0 as LatitudeCoordinate),
  time: z.string(),
  date: z.string(),
  track: z.string(),
  direction: z.string(),
  products: z.array(JOURNEY_PRODUCT_SCHEMA),
});
